import EditableList from '@/components/EditableList'
import { useFetchProfile } from '@/hooks/useFetchProfile'
import { normalizeHttpUrl } from '@/lib/url'
import SecondaryPageLayout from '@/layouts/SecondaryPageLayout'
import { useAtomValue } from 'jotai'
import { invalidateBlossom, blossomChannelAtom } from '@/atoms/invalidation'
import client from '@/services/client.service'
import { loadBlossomServers } from '@nostr/gadgets/lists'
import { forwardRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import * as kinds from '@nostr/tools/kinds'
import { now } from '@/lib/utils'
import { useAsync } from 'react-use'
import { useCurrentPubkey } from '@/atoms/session'
import { unwrappedBlossomServersAtom } from '@/atoms/lists'

const BlossomPage = forwardRef(({ id, index }: { id: string; index?: number }, ref) => {
  const { t } = useTranslation()
  const currentPubkey = useCurrentPubkey()
  const { profile } = useFetchProfile(id)
  const isSelf = profile?.pubkey === currentPubkey
  const channel = useAtomValue(blossomChannelAtom)
  const ourBlossomServers = useAtomValue(unwrappedBlossomServersAtom) ?? []

  useEffect(() => {
    if (!profile) return
    const name = profile.shortName || '...'
    document.title = t("{{name}}'s blossom servers on {{appName}}", {
      name,
      appName: window.hallway.universe.appName
    })
    history.replaceState(null, '')
  }, [profile])
  const { value: servers } = useAsync(
    async () =>
      profile
        ? profile.pubkey === currentPubkey
          ? ourBlossomServers
          : loadBlossomServers(profile.pubkey).then((bs) => bs.items)
        : [],
    [profile?.pubkey, channel]
  )

  return (
    <SecondaryPageLayout
      ref={ref}
      index={index}
      title={
        profile?.pubkey === currentPubkey
          ? t('Blossom Servers')
          : t("{{username}}'s blossom servers", { username: profile?.shortName })
      }
    >
      <div className="px-4 py-3">
        <EditableList
          items={servers || []}
          editable={isSelf}
          renderItem={(server) => (
            <a
              href={server}
              target="_blank"
              rel="noreferrer"
              className="block truncate text-sm hover:underline py-0.5"
            >
              {server}
            </a>
          )}
          validateItem={(val: string) => {
            if (!currentPubkey) return { ok: false as const, error: t('Login required') }
            const url = normalizeHttpUrl(val)
            if (!url) return { ok: false as const, error: t('Invalid URL') }
            return { ok: true as const, item: url }
          }}
          onUpdate={async (newServers: string[]) => {
            if (!currentPubkey) return

            const event = await client.publish({
              kind: kinds.BlossomServerList,
              content: '',
              tags: newServers.map((server) => ['server', server]),
              created_at: now()
            })
            if (!event) {
              console.warn('failed to publish updated blossom server list event')
              toast.error(t('Failed to publish updated Blossom server list event.'))
              return
            }
            await loadBlossomServers(event.pubkey, undefined, event)
            invalidateBlossom()
          }}
          placeholder={t('Enter Blossom server URL')}
          emptyMessage={t('No Blossom servers configured.')}
        />
      </div>
    </SecondaryPageLayout>
  )
})
BlossomPage.displayName = 'BlossomPage'
export default BlossomPage
